import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { useSEO } from "@/lib/useSEO";

const LAST_UPDATED = "9 August 2026";

const H2 = ({ children }) => (
  <h2 className="font-serif-display italic text-[#F5F4F0] text-2xl md:text-3xl font-light tracking-tight mt-14 mb-4 leading-tight">
    {children}
  </h2>
);
const H3 = ({ children }) => (
  <h3 className="text-[#C9A876] text-[10px] tracking-[0.32em] uppercase font-body mt-8 mb-3">
    {children}
  </h3>
);
const P = ({ children }) => (
  <p className="text-[#D6D1CA]/80 text-sm md:text-[15px] leading-[1.75] font-body mb-4">
    {children}
  </p>
);
const UL = ({ children }) => (
  <ul className="list-disc list-outside pl-5 space-y-2 text-[#D6D1CA]/80 text-sm md:text-[15px] leading-[1.75] font-body mb-4">
    {children}
  </ul>
);

export default function Terms() {
  useSEO({
    title: "Terms of Use · My Floor Studio",
    description:
      "The terms and conditions that apply when you use myfloorstudio.com.au — including intellectual property, indicative imagery, non-binding enquiries and NSW governing law.",
    path: "/terms",
    keywords: "my floor studio terms, terms of use, terms and conditions, luxury flooring terms Sydney",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://myfloorstudio.com.au/" },
          { "@type": "ListItem", position: 2, name: "Terms of Use", item: "https://myfloorstudio.com.au/terms" },
        ],
      },
    ],
  });
  return (
    <>
      <Nav />
      <main className="bg-[#121315] pt-32 pb-24" data-testid="terms-page">
        <div className="mx-auto max-w-3xl px-6 md:px-8">
          <p className="text-[#C9A876] text-[10px] tracking-[0.4em] uppercase mb-4">Legal</p>
          <h1 className="font-serif-display italic text-[#F5F4F0] text-4xl md:text-5xl font-light leading-none tracking-tight">
            Terms of Use
          </h1>
          <p className="text-[#D6D1CA]/60 text-xs tracking-[0.28em] uppercase font-body mt-6">
            Last updated · {LAST_UPDATED}
          </p>

          <div className="mt-12">
            <P>
              These Terms of Use govern your access to and use of the website{" "}
              <a href="https://myfloorstudio.com.au" className="text-[#C9A876] link-underline">myfloorstudio.com.au</a>{" "}
              (the “Site”), operated by My Floor Studio (<strong className="text-[#F5F4F0]">ABN 87 651 648 029</strong>).
              By accessing the Site you agree to be bound by these Terms. If you do not agree,
              please do not use the Site.
            </P>

            <H2>1. Nothing on the Site is a binding offer</H2>
            <P>
              All product information, imagery, pricing indications, sample availability and
              lead times shown on the Site are provided for guidance only. Nothing on the Site
              constitutes a binding offer to supply goods or services. A binding contract is
              formed only when we issue you a written quote and you sign and return it (or pay
              a deposit) in accordance with our supply terms.
            </P>

            <H2>2. Imagery is indicative</H2>
            <P>
              Every effort has been made to represent our timber, carpet, tile and stone as
              accurately as possible. Natural materials vary batch to batch, and screen
              renderings, sample images and visualiser mock-ups cannot capture true colour,
              grain, veining and texture with absolute accuracy. Always request a physical
              sample before making a final specification decision. Where a visualised room
              render is shown, the render is a computer-generated approximation and does not
              form part of any specification or supply.
            </P>

            <H2>3. Intellectual property</H2>
            <P>
              All content on the Site — including the “My Floor Studio” name and logo, the
              puzzle emblem, all photography, illustrations, room renders, colour palettes,
              copywriting and code — is owned by or licensed to My Floor Studio and is
              protected by Australian and international intellectual property laws.
            </P>
            <P>
              You may view and print pages from the Site for your personal, non-commercial
              reference (for example, a mood board or specification document). You may not
              reproduce, distribute, publish or use any content commercially without our prior
              written consent.
            </P>

            <H2>4. Trade &amp; commercial use</H2>
            <P>
              Architects, designers, builders and specifiers may share content from the Site
              with their clients as part of a project mood board or specification, provided
              attribution to My Floor Studio is preserved and the content is not modified in a
              way that misrepresents the product or the brand.
            </P>

            <H2>5. Sample kits &amp; appointments</H2>
            <P>
              Sample kits are provided free of charge to bona-fide project enquirers, subject to
              availability. We reserve the right to limit or decline any sample request. Studio
              appointments are confirmed by email; if you need to reschedule please give at
              least 24 hours’ notice.
            </P>

            <H2>6. Third-party links</H2>
            <P>
              The Site may link to third-party sites (for example, supplier pages, industry
              regulators or social platforms). We do not control and are not responsible for
              the content or practices of any third-party site.
            </P>

            <H2>7. Availability &amp; changes</H2>
            <P>
              We aim to keep the Site available at all times but do not guarantee
              uninterrupted access. We may add, remove, modify or discontinue any product,
              service or feature on the Site at any time and without notice.
            </P>

            <H2>8. Disclaimer of warranties</H2>
            <P>
              To the maximum extent permitted by law, the Site and all content are provided
              on an “as is” and “as available” basis. We make no warranties, express or
              implied, about the completeness, accuracy, reliability, suitability or
              availability of the Site or its content.
            </P>
            <P>
              Nothing in these Terms limits your rights under the Australian Consumer Law
              where those rights cannot lawfully be excluded, restricted or modified.
            </P>

            <H2>9. Limitation of liability</H2>
            <P>
              To the maximum extent permitted by law, My Floor Studio, its directors,
              employees and contractors are not liable for any indirect, incidental, special
              or consequential loss or damage arising out of or in connection with your use of
              the Site. Where liability cannot be excluded, our aggregate liability to you
              is limited to the resupply of the relevant service or the amount you paid us
              for that service, at our option.
            </P>

            <H2>10. Privacy</H2>
            <P>
              Our collection and handling of personal information is governed by our{" "}
              <a href="/privacy" className="text-[#C9A876] link-underline">Privacy Policy</a>,
              which forms part of these Terms.
            </P>

            <H2>11. Governing law &amp; jurisdiction</H2>
            <P>
              These Terms are governed by the laws of New South Wales, Australia. You submit
              to the exclusive jurisdiction of the courts of New South Wales and the courts
              of appeal from them.
            </P>

            <H2>12. Contact</H2>
            <div className="mt-2 border border-[#8C7355]/20 p-6 bg-[#0f1012]">
              <H3>My Floor Studio</H3>
              <P>184 Victoria Road, Drummoyne NSW 2047</P>
              <P>
                <a href="tel:+61272467899" className="text-[#C9A876] link-underline">02 7246 7899</a>{" "}
                &nbsp;·&nbsp;{" "}
                <a href="mailto:info@myfloorstudio.com.au" className="text-[#C9A876] link-underline">info@myfloorstudio.com.au</a>
              </P>
              <P>ABN 87 651 648 029</P>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
